// src/encounter/rail-v3/rail-v3-actions.ts
// Which row actions the rail-v3 rail offers, per section, stage and persona: the tables of the left-rail target, 4 Nov 2026 §3
// (docs/left-rail-target-2026-11-04.md; Elation actions approved 4 Nov). A plain module (no component, no look): it returns the
// LABELS in the hover card's order and marks which ones the hover bar carries. `patient-summary-rail.tsx` binds each label to the
// handler it already has. Who may act comes from the existing server-backed helpers, passed in: `useRole().clinicalRole` (persona),
// `mayOrder` + order type and `mayStopOrSuspend` (the host passes `onMedicationToPlan` only then), and the A&P write gate
// (`mayAddProblemToNote`, the host passes `onAddProblemToNote` only then). No role check is written here beyond reading those.
import type { IpsAction } from '@scripsteam/scrips-signal-ds';
import type { ClinicalRole } from '@/auth/use-role';

/** A rail-v3 row action: the DS action, plus whether the hover bar carries it and, when it cannot run, why (shown as its tooltip). */
export interface RailV3Action extends IpsAction {
  /** The hover bar carries it (§3: the bar is the non-destructive subset with an icon; the card carries all). */
  bar: boolean;
  /** Set when the action cannot run now; the control is then disabled and says why. */
  disabledReason?: string;
}

/** One planned action: its label (the handler key) and its place. `danger` = red, last in the card. */
export interface RailV3PlannedAction {
  label: RailV3ActionLabel;
  bar: boolean;
  danger?: boolean;
}

export type RailV3ActionLabel =
  | 'Refill'
  | 'Edit dose'
  | 'Add to note'
  | 'Edit'
  | 'Reconcile medications'
  | '+ Note'
  | 'Resolve'
  | 'Add to encounter diagnoses'
  | 'Entered in error'
  | 'Unresolve'
  | 'Undo resolve'
  | 'Remove';

export interface RailV3ActionContext {
  /** No A&P exists before Start: no Plan action, no "Add to note" into the encounter note. */
  stage: 'pre-visit' | 'started';
  /** `useRole().clinicalRole`. */
  persona: ClinicalRole;
  /** The host passed `onMedicationToPlan` (a started, unsigned visit and a user who may order medications, #906). */
  mayPlanMedications: boolean;
  /** `useRole().mayStopOrSuspend` (PROD-2669). Kept for the pending Stop line once it exists (Stop is not offered yet, see below). */
  mayStopMedication: boolean;
  /** The host passed `onAddProblemToNote` (the A&P's own writer, A&P loaded and editable: `mayAddProblemToNote`). */
  apWriter: boolean;
  /** The host passed `onReconcileMedications` (the reconciliation card from #916 is on this page). */
  mayReconcile: boolean;
}

/** Medications (§3, with the FINAL bullet of §4b). A stopped row has no actions; the caller keeps the mapper's empty list for it.
 *  Elation's set: Refill · Edit dose (Document change) · Reconcile · Add to note (Export to Note) · Stop with reason. "Continue" is
 *  not an Elation action and is not offered. Stop is NOT offered from the rail yet: §3 says it creates a PENDING A&P Plan line, with a
 *  required reason, committed at sign, and the only stop path today (#906 `ApMedStopHost` → PostStopMedication) writes the medication
 *  record at once with an optional reason. Until a pending discontinue line exists (a gap for Andrew), offering it would write a
 *  clinical fact before the note is signed. */
export function railV3MedicationActions(ctx: RailV3ActionContext): RailV3PlannedAction[] {
  const out: RailV3PlannedAction[] = [];
  const started = ctx.stage === 'started';
  // Prescriber, started, not signed: bar Refill · Edit dose · Add to note.
  if (started && ctx.mayPlanMedications) out.push({ label: 'Refill', bar: true }, { label: 'Edit dose', bar: true });
  // Reconcile medications: physician and nurse (§4b), wherever the host has the reconciliation card (it passes the handler only then).
  if (ctx.mayReconcile && (ctx.persona === 'physician' || ctx.persona === 'nurse')) out.push({ label: 'Reconcile medications', bar: false });
  if (started) out.push({ label: 'Add to note', bar: true });
  // Edit (the MedicationStatement, not an order) stays in the card, for the physician and the nurse.
  if (ctx.persona === 'physician' || ctx.persona === 'nurse') out.push({ label: 'Edit', bar: false });
  return out;
}

/** Active problems (§3). */
export function railV3ProblemActions(ctx: RailV3ActionContext): RailV3PlannedAction[] {
  const physician = ctx.persona === 'physician';
  if (ctx.stage === 'pre-visit') {
    return [
      { label: '+ Note', bar: true },
      ...(physician ? [{ label: 'Resolve' as const, bar: true }] : []),
      { label: 'Edit', bar: true },
    ];
  }
  const out: RailV3PlannedAction[] = [{ label: 'Add to note', bar: true }];
  if (physician) out.push({ label: 'Resolve', bar: true });
  out.push({ label: 'Edit', bar: true });
  if (physician && ctx.apWriter) out.push({ label: 'Add to encounter diagnoses', bar: false });
  if (physician) out.push({ label: 'Entered in error', bar: false, danger: true });
  return out;
}

/** Resolved problems (§3): Reactivate · Undo resolve (only right after a resolve, ruling 3) · Edit · Remove (it exists today).
 *  Reactivate and Undo resolve put a medical problem back on the active list, so they follow Resolve's gate: the user who may
 *  resolve (`canResolve`: not a nurse or allied health, who may not accept or enter a medical diagnosis — the authority's persona
 *  rule). Edit and Remove keep today's gate. */
export function railV3ResolvedActions(opts: { justResolved: boolean; canResolve: boolean }): RailV3PlannedAction[] {
  return [
    ...(opts.canResolve ? [{ label: 'Unresolve' as const, bar: true }] : []),
    ...(opts.canResolve && opts.justResolved ? [{ label: 'Undo resolve' as const, bar: true }] : []),
    { label: 'Edit', bar: true },
    { label: 'Remove', bar: false, danger: true },
  ];
}

/** Bind planned labels to handlers. A label with no handler is dropped (an affordance with no host is never drawn). */
export function railV3BindActions(
  plan: readonly RailV3PlannedAction[],
  handlers: Partial<Record<RailV3ActionLabel, () => void>>,
  disabled: Partial<Record<RailV3ActionLabel, string | undefined>> = {},
): RailV3Action[] {
  return plan.flatMap((p) => {
    const onClick = handlers[p.label];
    if (!onClick) return [];
    const reason = disabled[p.label];
    return [{ label: p.label, onClick, bar: p.bar, ...(p.danger ? { tone: 'danger' as const } : {}), ...(reason ? { disabledReason: reason } : {}) }];
  });
}

/** A planned action (from `railV3BindActions`) rather than a legacy one: it says itself whether the bar carries it. */
export function isRailV3Action(a: IpsAction): a is RailV3Action {
  return typeof (a as Partial<RailV3Action>).bar === 'boolean';
}
